import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Typography,
  Box,
  Alert
} from '@mui/material';
import { useSnackbar } from 'notistack';
import { updateTokenRequest, clearTokensError } from '../../redux/actions/auth/apiTokens';
import { MAX_TOKEN_NAME_LENGTH } from './apiTokenConstants';
import ScopePicker from './ScopePicker';

/**
 * Edit a token's name and scopes. The secret itself never changes here -
 * rotating the secret is what "Regenerate" is for.
 */
const EditAPITokenDialog = ({ open, token, onClose }) => {
  const [name, setName] = useState('');
  const [scopes, setScopes] = useState([]);
  const submittedRef = useRef(false);

  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const { isUpdating, error } = useSelector((state) => state.auth.apiTokens);

  // Seed the form from the token each time the dialog opens
  useEffect(() => {
    if (open && token) {
      setName(token.name || '');
      setScopes(token.scopes || []);
      submittedRef.current = false;
      dispatch(clearTokensError());
    }
  }, [open, token, dispatch]);

  // Close once our submission has finished successfully
  useEffect(() => {
    if (submittedRef.current && !isUpdating) {
      if (!error) {
        enqueueSnackbar('Token updated', { variant: 'success' });
        onClose();
      }
      submittedRef.current = false;
    }
  }, [isUpdating, error, enqueueSnackbar, onClose]);

  const handleScopeChange = (scope) => {
    setScopes((prev) =>
      prev.includes(scope) ? prev.filter((s) => s !== scope) : [...prev, scope]
    );
  };

  const handleScopesBulkChange = (values, checked) => {
    setScopes((prev) =>
      checked
        ? [...new Set([...prev, ...values])]
        : prev.filter((s) => !values.includes(s))
    );
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!token || isUpdating) return;

    submittedRef.current = true;
    dispatch(updateTokenRequest(token.token_id, {
      name: name.trim(),
      scopes
    }));
  };

  const nameTooLong = name.trim().length > MAX_TOKEN_NAME_LENGTH;
  const canSubmit = !isUpdating
    && name.trim().length > 0
    && !nameTooLong
    && scopes.length > 0;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Edit API Token</DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {token && (
              <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                {token.token_id}
              </Typography>
            )}

            <TextField
              fullWidth
              autoFocus
              label="Token Name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              error={nameTooLong}
              helperText={
                nameTooLong
                  ? `Name must be at most ${MAX_TOKEN_NAME_LENGTH} characters`
                  : undefined
              }
            />

            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Permissions (Scopes)
              </Typography>
              <ScopePicker
                columns
                selected={scopes}
                onToggle={handleScopeChange}
                onToggleMany={handleScopesBulkChange}
              />
              {scopes.length === 0 && (
                <Typography variant="caption" color="error">
                  Select at least one scope
                </Typography>
              )}
            </Box>

            {error && (
              <Alert severity="error">
                {typeof error === 'string' ? error : error.message || 'Failed to update token'}
              </Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={isUpdating}>Cancel</Button>
          <Button type="submit" variant="contained" disabled={!canSubmit}>
            {isUpdating ? 'Saving...' : 'Save Changes'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default EditAPITokenDialog;
